import { useState, type FormEvent } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../auth/AuthProvider';
import { acceptInvite } from '../lib/api';
import { db } from '../lib/supabase';
import { friendlyError } from '../lib/format';
import { Button, ErrorText, TextField } from '../components/ui';

export function AcceptInvite() {
  const { token = '' } = useParams();
  const { session, me, reload, signOut } = useAuth();
  const [fullName, setFullName] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  if (!session) return <Navigate to={`/login?next=${encodeURIComponent(`/invite/${token}`)}`} replace />;
  if (me) return <Navigate to="/leads" replace />;

  // Someone who arrived from an invitation email has no password yet.
  const u = session.user as { invited_at?: string; user_metadata?: { password_set?: boolean } };
  const needsPassword = !!u.invited_at && !u.user_metadata?.password_set;

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true); setError('');
    try {
      if (needsPassword) {
        const { error: pe } = await db().auth.updateUser({ password, data: { password_set: true } });
        if (pe) throw new Error(pe.message);
      }
      await acceptInvite(token, fullName);
      await reload();
      navigate('/leads', { replace: true });
    } catch (err) {
      setError(friendlyError(err));
      setBusy(false);
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center px-4">
      <div className="w-full max-w-[420px]">
        <p className="font-display text-3xl">Join your team</p>
        <p className="mb-6 text-muted">Signed in as {session.user.email}. The invite must be for this email.</p>
        <form onSubmit={submit} className="flex flex-col gap-4 rounded-2xl border border-line bg-surface p-5">
          <TextField label="Your name" required value={fullName} onChange={(e) => setFullName(e.target.value)} />
          {needsPassword && <TextField label="Choose a password" type="password" autoComplete="new-password" required minLength={8} value={password}
            onChange={(e) => setPassword(e.target.value)} hint="At least 8 characters. You sign in with your email and this password next time." />}
          <ErrorText>{error}</ErrorText>
          <Button variant="primary" type="submit" disabled={busy}>{busy ? 'Joining…' : 'Join'}</Button>
        </form>
        <p className="mt-4 text-center text-sm text-muted"><button className="hover:text-accent" onClick={signOut}>Use a different account</button></p>
      </div>
    </div>
  );
}
